import React, { FC, useCallback } from 'react';
import translate from '@translate';
import BodyTopBlock, { ActionType } from 'src/components/BodyTopBlock';
import { ExportTypeType, SelectRowType } from 'src/types/table';
import ExportTableAction from 'src/components/ExportTableAction';
import StoreIcon from '@mui/icons-material/Store';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import FlipIcon from '@mui/icons-material/Flip';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import AssignmentReturnedIcon from '@mui/icons-material/AssignmentReturned';
import { AddIcon } from 'src/components/SkladonIcon/Icons';
import { useNavigate } from 'react-router-dom';
import { ImportIcon } from 'src/components/SkladonIcon/SmallIcons';
import { ConstantsCodesObjectType, ConstantsCodeType } from '../../types/constantsCodes';
import { checkPermission, useCheckPermission } from '../../utils/checkPermission';
import filterUnique from '../../utils/filterUnique';
import { ReceivingType } from '../../types/receiving';
import { getAllowedStatuses } from '../ReceivingDetailContainer/functions';

type TopBlockProps = {
    handleExport: (type: ExportTypeType, withProducts?: boolean) => void;
    allProviders: boolean;
    setAllProviders: React.Dispatch<React.SetStateAction<boolean>>;
    reloadTableData: () => void;
    handleMassChangeStatus: (statuses: ConstantsCodeType[]) => void;
    selectedRows: SelectRowType[];
    permissionReceivingCreate: boolean;
    permissionReceivingUpdate: boolean;
    permissionSynchronizeDcix: boolean;
    handleSynchronizeDCIX: () => void;
    permissionAllProviders: boolean;
    receivingsDataData?: ReceivingType[];
    receivingStatusCodes: ConstantsCodeType[];
    receivingStatusCodesObject: ConstantsCodesObjectType;
};

const TopBlock: FC<TopBlockProps> = (props) => {
    const getEnableMultipleActions = (): {
        ableEditStatuses: boolean;
        allowedStatuses: ConstantsCodeType[];
    } | null => {
        if (!props.selectedRows.length || !props.receivingsDataData) {
            return null;
        }

        let ableEditStatuses = props.permissionReceivingUpdate;

        const selectedStatuses: string[] = [];

        props.receivingsDataData
            .filter((item) => props.selectedRows.includes(String(item.ID)))
            .forEach((item) => {
                if (item.STATUS) {
                    selectedStatuses.push(item.STATUS);
                }
            });

        let allowedStatuses: ConstantsCodeType[] = [];
        if (ableEditStatuses) {
            selectedStatuses.filter(filterUnique).forEach((status, index) => {
                if (index === 0) {
                    allowedStatuses = getAllowedStatuses(
                        props.receivingStatusCodes,
                        props.receivingStatusCodesObject,
                        { STATUS: status },
                    );
                } else {
                    const thisStatusStatuses = getAllowedStatuses(
                        props.receivingStatusCodes,
                        props.receivingStatusCodesObject,
                        { STATUS: status },
                    ).map((status) => status.value);

                    allowedStatuses = allowedStatuses.filter((status) => thisStatusStatuses.includes(status.value));
                }
            });
        }
        return {
            ableEditStatuses,
            allowedStatuses
        };
    };

    const navigate = useNavigate();
    const permissionExportReceivings = useCheckPermission('RECEIVING.EXPORT.TABLE');
    const permissionExportReceivingsProducts = useCheckPermission('RECEIVING.EXPORT.PRODUCTS');

    const handleNew = useCallback(() => {
        navigate(`/receivings/new/`);
    }, []);

    const handleImport = useCallback(() => {
        navigate(`/receivings/import/`);
    }, []);

    const ableMultipleActions = getEnableMultipleActions();
    const actions: ActionType[] = [
        {
            onClick: handleImport,
            icon: <ImportIcon />,
            tooltip: translate('receivings.action.importReceiving'),
            variant: 'outlined',
            hidden: !props.permissionReceivingCreate || props.allProviders,
        },
        {
            node: (
                <ExportTableAction
                    title={translate('receivings.action.exportFile')}
                    handleExport={(type) => props.handleExport(type)}
                />
            ),
            hidden: !permissionExportReceivings,
        },
        {
            onClick: () => props.setAllProviders(!props.allProviders),
            icon: <StoreIcon />,
            tooltip: translate('receivings.allProviders'),
            variant: 'outlined',
            active: props.allProviders,
            hidden: !props.permissionAllProviders,
        },
        {
            onClick: () => props.reloadTableData(),
            icon: <AutorenewIcon />,
            tooltip: translate('receivings.action.synchronizeTable'),
            variant: 'outlined',
        },
        {
            items: [
                {
                    onClick: () => props.handleSynchronizeDCIX(),
                    icon: <SyncAltIcon />,
                    label: translate('receivings.action.synchronizeDcix'),
                    variant: 'text',
                    hidden: !props.selectedRows.length || !props.permissionSynchronizeDcix,
                },
                { divider: true },

                {
                    node: (
                        <ExportTableAction
                            title={translate('receivings.action.exportWithProducts')}
                            handleExport={(type) => props.handleExport(type, true)}
                            icon={<AssignmentReturnedIcon />}
                            textWithIcon
                        />
                    ),
                    hidden: props.allProviders || !permissionExportReceivingsProducts,
                },
                {
                    onClick: () =>
                        ableMultipleActions?.allowedStatuses &&
                        props.handleMassChangeStatus(ableMultipleActions?.allowedStatuses),
                    icon: <FlipIcon />,
                    label: translate('receivings.action.changeStatuses'),
                    variant: 'text',
                    hidden: !ableMultipleActions?.allowedStatuses.length
                },
            ],
        },
        {
            label: translate('receivings.action.createNew'),
            onClick: handleNew,
            icon: <AddIcon />,
            hidden: props.allProviders || !props.permissionReceivingCreate,
        },
    ];

    return <BodyTopBlock actions={actions} />;
};

export default TopBlock;
